import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom'; // Extract assistantId from query string
import './Header.scss';
import '../../styles/ui-buttons.scss';
import HistoryModal from '../history/HistoryModal'; // Import the HistoryModal component
import { getImageById, NO_AVATAR_PIC } from '../../utils/botFactoryAPI'; //
import { AvatarImage, AccountAvatarSection } from './AccountAvatar';
import { setFavicon, loadFirstImage } from '../../utils/favicon';
import { iconSources } from '../../utils/orgIcon';
import { BOT_SETTINGS_STORED_EVENT } from '../../utils/botIdentity';
import { getMicMode, setMicMode, MicMode } from '../../lib/micMode';

interface BotSettings {
  showThumb?: boolean;
}

interface SelectedBot {
  botid?: string;
  thumbnail?: string;
  title?: string;
  name?: string;
  settings?: BotSettings;
  header?: { link?: string; text?: string }; // the agent's Home setting
}

interface HeaderProps {
  onSettingsChange?: (settings: any | null) => void;
  iBots?: { iBots: SelectedBot[] }[];
}

const GDS_AGENT_IDS = ['agent_5nSiXDMAwKQoh8QIptFeMg'];

export function Header({ iBots = [], onSettingsChange }: HeaderProps) {
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const assistantId = useMemo(() => queryParams.get('id') || '', [queryParams]);
  const isGdsAgent = GDS_AGENT_IDS.includes(assistantId); // the GDS hover text applies to GDS agents only

  const [user, setUserState] = useState({
    id: '',
    firstname: '',
    lastname: '',
    email: '',
    pic: '',
    username: '',
    org: '',
  });

  const [selectedBot, setSelectedBot] = useState<SelectedBot | null>(null);
  const [guestMode, setGuestMode] = useState(false);

  const hamburgerPicWhite = "/assets/app/hamburger-white.png";
  const hamburgerPicBlack = "/assets/app/hamburger-black.png";
  const hamburgerPicGrey = "/assets/app/hamburger-grey.png";
  //const homePicWhite = "/assets/app/home-white.png";


  const logoPicWhite = ""; // Use the org logo instead of a static image
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false); // Settings panel (microphone mode)
  const [micModeChoice, setMicModeChoice] = useState<MicMode>(getMicMode);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [settings, setSettings] = useState<any>({});

  

  const [org, setOrg] = useState<any>(() => {
    let storedOrg = localStorage.getItem('org');
    if (storedOrg) {
      try {
        return JSON.parse(storedOrg);
      } catch {
        return null;
      }
    }
    return null;
  });

  // The home picture is the agent's square icon (see iconSources), else its logo, else the default.
  const [homePicWhite, setHomePic] = useState<string>(() => org?.favicon || org?.logo || "/assets/app/home-white.png");
  useEffect(() => {
    let cancelled = false;
    loadFirstImage(iconSources(org, ['/assets/app/home-white.png'])).then((img) => {
      if (!cancelled && img) setHomePic(img.src);
    });
    return () => { cancelled = true; };
  }, [org]);

  const hideAvatarButton = useMemo(() => {
    return guestMode === true || settings?.type?.guestmode === true || org?.type?.guestmode === true;
  }, [guestMode, settings?.type?.guestmode, org?.type?.guestmode]);

  // Resolve the selected bot so we can surface its metadata (used for share previews).
  useEffect(() => {
    try {
      let nextBot: SelectedBot | null = null;

      if (assistantId && iBots.length > 0) {
        for (const group of iBots) {
          for (const bot of group.iBots) {
            if (bot?.botid === assistantId) {
              nextBot = bot;
              break;
            }
          }
          if (nextBot) break;
        }
      }

      // Fallback to cached selection if we didn't find a match in props
      if (!nextBot) {
        const stored = localStorage.getItem('SelectedBot');
        if (stored) {
          nextBot = JSON.parse(stored);
        }
      }

      if (nextBot?.botid && nextBot.botid !== selectedBot?.botid) {
        setSelectedBot(nextBot);
      }
    } catch {
      // Ignore parsing errors and leave selection unchanged
    }
  }, [assistantId, iBots, selectedBot?.botid]);

  // Effect to update org and settings based on assistantId
  // [S2-B B2] Header RENDERS the branding the page stored from the record — B1c's allowlisted
  // `botSettings` payload (botIdentity.buildBotSettingsPayload, keyed by this bot's botid) — and
  // the pre-auth pages' `org` (agentBootstrap.orgFromBootstrap). It no longer imports settings.json,
  // no longer overwrites the page's payload with a disk entry, and never WRITES botSettings: the
  // only writer is the page (botIdentity.storeBotSettings). Re-reads when the bot changes AND when
  // the page announces the store (BOT_SETTINGS_STORED_EVENT → storedBrandingTick): the page stores
  // the payload after its record fetch, which is after this mounts, and on a repeat visit to the
  // same bot nothing else here changes. The deps hold NO per-render value (no iBots, no org — the
  // 60505ec render loop), and both setters below are no-ops when the value is unchanged.
  const [storedBrandingTick, setStoredBrandingTick] = useState(0);
  useEffect(() => {
    const onStored = (e: Event) => {
      const botid = (e as CustomEvent<{ botid?: string }>).detail?.botid;
      if (botid && botid === assistantId) setStoredBrandingTick((t) => t + 1);
    };
    window.addEventListener(BOT_SETTINGS_STORED_EVENT, onStored);
    return () => window.removeEventListener(BOT_SETTINGS_STORED_EVENT, onStored);
  }, [assistantId]);

  useEffect(() => {
  try {
    const readJson = (k: string) => {
      try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; }
    };
    const stored = readJson('botSettings');
    const matched = stored && assistantId && stored.botid === assistantId ? stored : null;
    console.log('[Header] Read stored botSettings for assistantId:', assistantId, matched);
    
    let newOrg = org;

    if (!assistantId) {
      newOrg = { name: 'Admin Page', logo: '/assets/app/favicon.png', homepage: '/' };
    } else if (matched) {
      newOrg = matched;
    }

    if (JSON.stringify(newOrg) !== JSON.stringify(org)) {
      setOrg(newOrg);
      localStorage.setItem('org', JSON.stringify(newOrg));
    }

    const nextSettings = matched || {};

    setSettings((current: any) => {
      if (JSON.stringify(current) === JSON.stringify(nextSettings)) {
        return current;
      }

      return nextSettings;
    });

    if (matched) {
      console.log(
        '[Header][Share] Record branding for assistant:',
        assistantId,
        matched?.title || matched?.name
      );
    }
  } catch (e) {
    console.error('❌ [Header] failed to read the stored record branding', e);
  }
}, [assistantId, selectedBot?.botid, storedBrandingTick]);

  // Effect to update guest mode and apply dynamic settings on org or settings change
  useEffect(() => {
    if (!org) return;

    const guestModeStr = localStorage.getItem('guestMode');
    setGuestMode(guestModeStr === 'true');

    try {
      // One favicon source: the agent's square icon (see iconSources), else its logo. Never stretched.
      setFavicon(iconSources(org, [selectedBot?.thumbnail, '/favicon.png']));

      document.title = org.name;

      const headerEl = document.querySelector('header');
      if (headerEl) headerEl.style.background = settings.headerBackColor;

      const logoImg = document.getElementsByClassName('header-logo');

      if (logoImg.length === 0) {
        console.log("❌ [Header] No logo image found. Please check the class name.");
        return;
      }
      if (logoImg.length > 0) (logoImg[0] as HTMLImageElement).setAttribute('src', org.logo);

      const logoAnchor = document.querySelector('.header-logo-container a');
      const homeAnchor = document.querySelector('.home-button a');

      if (logoAnchor) logoAnchor.setAttribute('href', org.homepage);
      if (homeAnchor) homeAnchor.setAttribute('href', org.homepage);

      const mainBackcolor = document.querySelector('main');
      if (mainBackcolor) mainBackcolor.style.backgroundColor = org.containerStyle.backgroundColor;

      const containerBackcolor = document.getElementsByClassName('chat-container')[0];
      if (containerBackcolor) {
        try {
          (containerBackcolor as HTMLElement).style.backgroundColor = org.containerStyle.backgroundColor;
        } catch (e) {
          console.log("[Header] No setting for containerStyle background color found:", e);
        }
      }

      const cardsContainer = document.getElementsByClassName('cards-container')[0];
      if (cardsContainer) {
        try {
          (cardsContainer as HTMLElement).style.backgroundColor = org.chatStyle.backgroundColor;
        } catch (e) {
          console.log("[Header] No setting for chatStyle background color found:", e);
        }
      }

      const cardButtons = document.getElementsByClassName('card')[0];
      if (cardButtons) {
        try {
          (cardButtons as HTMLElement).style.backgroundColor = org.buttonStyle.backgroundColor;
        } catch (e) {
          console.log("[Header] No setting for buttonStyle background color found:", e);
        }
      }

      const cardTextColor = document.getElementsByClassName('card')[0];
      if (cardTextColor) {
        try {
          (cardTextColor as HTMLElement).style.color = org.buttonStyle.color;
        } catch (e) {
          console.log("[Header] No setting for buttonStyle color found:", e);
        }
      }

      const footerBackcolor = document.querySelector('footer');
      if (footerBackcolor) footerBackcolor.style.backgroundColor = org.footer.backcolor;
    } catch (err) {
      console.log("❌ [Header] Error applying dynamic settings:", err);
    }
  }, [org, settings]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUserState(JSON.parse(storedUser));
    }
  }, []);

  // React to live language-mode updates dispatched by IndexPage after translation
  useEffect(() => {
    const onUpdated = (e: Event) => {
      const detail = (e as CustomEvent<any>).detail;
      if (!detail) return;
      setSettings((prev: any) => ({
        ...prev,
        ...(detail.subtitle !== undefined ? { subtitle: detail.subtitle } : {}),
        ...(detail.flyout   !== undefined ? { flyout:   detail.flyout   } : {}),
      }));
    };

    const onReverted = (e: Event) => {
      const detail = (e as CustomEvent<any>).detail;
      if (!detail) return;
      // Restore the original subtitle/flyout from the pre-translation snapshot
      setSettings((prev: any) => ({
        ...prev,
        ...(detail.subtitle !== undefined ? { subtitle: detail.subtitle } : {}),
        ...(detail.flyout   !== undefined ? { flyout:   detail.flyout   } : {}),
      }));
    };

    window.addEventListener('botSettingsUpdated', onUpdated);
    window.addEventListener('botSettingsReverted', onReverted);
    return () => {
      window.removeEventListener('botSettingsUpdated', onUpdated);
      window.removeEventListener('botSettingsReverted', onReverted);
    };
  }, []);

  useEffect(() => {
    const isHomeRoute = location.pathname.includes('home') || !selectedBot;
    const botThumbnail = isHomeRoute ? '/robot-factory-banner-white.jpg' : selectedBot?.thumbnail || '/robot-factory-banner-white.jpg';

    const updateMetaTags = () => {
      let metaTag = document.querySelector('meta[property="og:image"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', 'og:image');
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', botThumbnail);
      console.log('[Header][Share] og:image set to:', botThumbnail);

      const shareTitle =
        selectedBot?.title ||
        selectedBot?.name ||
        settings?.title ||
        settings?.name ||
        org?.name ||
        document.title ||
        'Assistant';
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', shareTitle);
      console.log('[Header][Share] og:title set to:', shareTitle, {
        selectedBot,
        settingsTitle: settings?.title || settings?.name,
        orgName: org?.name,
        documentTitle: document.title,
      });

      let appleIconTag = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!appleIconTag) {
        appleIconTag = document.createElement('meta');
        appleIconTag.setAttribute('name', 'apple-mobile-web-app-title');
        document.head.appendChild(appleIconTag);
      }
      appleIconTag.setAttribute('content', botThumbnail);
    };

    updateMetaTags();
  }, [selectedBot, settings, org, location.pathname]);

  // Home destination: the agent's own setting, read from memory (never from local storage).
  // The agent record's header link comes first, then the stored branding the header already holds.
  const homeUrl: string = selectedBot?.header?.link || settings?.homepage || org?.homepage || '';
  const openHome = () => {
    if (!homeUrl) return;
    window.open(homeUrl, '_self');
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Settings (hamburger menu): the microphone mode, for every agent. The choice is saved per user
  // and device in src/lib/micMode.ts, and the chat pages pick it up through its change event.
  const openSettings = () => {
    setMicModeChoice(getMicMode());
    setIsMenuOpen(false);
    setIsSettingsOpen(true);
  };

  // The choice is held here until OK: Cancel (or the X, or a click outside) leaves the saved mode as it was.
  const chooseMicMode = (mode: MicMode) => setMicModeChoice(mode);

  const saveSettings = () => {
    setMicMode(micModeChoice);
    setIsSettingsOpen(false);
  };

  const cancelSettings = () => setIsSettingsOpen(false);

  // Account (settings panel, signed-in users): the picture is saved as soon as it is changed, not on OK.
  const authToken = document.cookie.split('; ').find((row) => row.startsWith('token='))?.split('=')[1] || '';
  const showAccount = !!user.id && !!authToken && !hideAvatarButton;
  const avatarShown = user.pic && user.pic !== NO_AVATAR_PIC ? avatarUrl : undefined; // undefined: the silhouette
  const onAvatarChanged = (pic: string) => {
    const next = { ...user, pic };
    setUserState(next);
    localStorage.setItem('user', JSON.stringify(next));
  };

  useEffect(() => {
    async function fetchAvatarUrl() {
      if (!user?.pic) {
        setAvatarUrl(undefined);
        return;
      }
      const pic = user.pic;
      const isPicId = /^[a-f0-9]{24}$/.test(pic);
      if (isPicId) {
        try {
          const token = document.cookie
            .split('; ')
            .find(row => row.startsWith('token='))
            ?.split('=')[1];

          if (!token) {
            console.error('No auth token found for getImageById');
            setAvatarUrl(undefined);
            return;
          }

          const imageData = await getImageById(pic, token);
          setAvatarUrl(imageData?.url ?? undefined);
        } catch {
          setAvatarUrl(undefined);
        }
      } else {
        setAvatarUrl(pic);
      }
    }
    fetchAvatarUrl();
  }, [user?.pic]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!isMenuOpen) return;
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMenuOpen]);

  const hamburgerSrc = useMemo(() => {
    const preference = (settings?.hamburgerPic || '').toLowerCase();
    if (preference === 'black') return hamburgerPicBlack;
    if (preference === 'grey' || preference === 'gray') return hamburgerPicGrey;
    if (preference === 'white') return hamburgerPicWhite;
    if (assistantId === 'asst_M3yCPGj7R4YKciQ4ZyNNyzxT') return hamburgerPicWhite;
    return isMenuOpen ? hamburgerPicGrey : hamburgerPicWhite;
  }, [assistantId, isMenuOpen, settings?.hamburgerPic]);

  return (
    <>
      <header>
        <div className="home-button-container" id="home-button-container">
          <button
            className="home-button"
            title={isGdsAgent ? undefined : 'Company Home'}
            data-tooltip={isGdsAgent ? 'GDS Website' : undefined}
            onClick={() => {
              window.clearAssistantSession(); // ✅ global method call
              openHome();
            }}
          >
            <img
              src={org?.bot||homePicWhite}
              alt="Home"
              className="homePic-image"
              data-label="Home Button"
            />
          </button>

        </div>

        <div className="header-logo-container" id="header-logo-container">
          <a
            onClick={openHome}
            target="_self"
            rel="noopener noreferrer"
          >
            <img id="header-logo" className="header-logo header-toolbar-image" src={logoPicWhite} alt="Logo" />
          </a>
        </div>

        <div
          className="avatar-button-container"
          id="avatar-button-container"
          style={{ display: hideAvatarButton ? 'none' : undefined }}
        >
          <button
            className="avatar-button"
            id="avatar-button"
            title="Sign Out"
           onClick={() => {
              // 🧹 Clear assistant session globally
              if (window.clearAssistantSession) {
                window.clearAssistantSession();
              }

              if (guestMode === true && settings?.homepage) {
                window.location.href = settings.homepage;
              } else {
                window.location.assign(`/signin?id=${assistantId ?? ''}`);
              }
            }}
          >
            <AvatarImage src={avatarShown} alt="Sign Out" className="avatar-image  header-toolbar-image" id="avatar-image" />
          </button>
        </div>

        <div className="hamburgerPic-button-container" id="hamburgerPic-button-container">
          <button
            className="hamburgerPic-button"
            id="hamburgerPic-button"
            title="Menu"
            ref={menuButtonRef}
            onClick={toggleMenu}
          >
            <img
              src={hamburgerSrc}
              alt="Menu"
              className="hamburgerPic-image header-toolbar-image"
              data-label="Home Button"
            />
          </button>
          <div
            className={`flyout-menu ${isMenuOpen ? 'open' : ''}`}
            data-name="flyout-menu"
            ref={menuRef}
          >
            <ul className="flyout-list" id="flyout-list">
              <li data-name="flyout-settings-mic" aria-label="Settings">
                <button type="button" className="flyout-button" data-name="flyout-link-settings-mic" onClick={openSettings}>
                  Settings
                </button>
              </li>
              {/*
              <li
                data-name="flyout-history"
                aria-label="Flyout History"
                onClick={() => {
                  setHistoryOpen(true);
                  setIsMenuOpen(false);
                }}
              >
                {!guestMode && (
                  <span className="history-flyout-item">History</span>
                )}
              </li>
              <hr id="hr-flyout" />
              */}
              {settings?.flyout?.urls ? (
                settings.flyout.urls.map((item: { href: string | undefined; label: React.ReactNode; target?: string; }, index: React.Key | null | undefined) => {
                  const labelText = typeof item.label === 'string' ? item.label : String(item.label);
                  const linkTarget = typeof item.target === 'string' ? item.target : undefined;
                  return (
                  <li key={index} data-name={`flyout-${item.label || `item-${index}`}`} aria-label={labelText || undefined}>
                      <a
                        href={item.href}
                        target={linkTarget || '_self'}
                        rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                        data-name={`Header Flyout Link ${labelText || `item-${index}`}`}
                        aria-label={labelText || 'flyout menu ${item.label'}>
                        {item.label}
                      </a>
                      <hr />
                    </li>
                  );
                })
              ) : (
                <>
                  <li data-name="flyout-goals" aria-label="My Goals & Objectives">
                    <a href="/goals" data-name="flyout-link-goals" aria-label="My Goals & Objectives">My Goals & Objectives</a><hr />
                  </li>
                  <li data-name="flyout-who" aria-label="Who Not How">
                    <a href="/who" data-name="flyout-link-who" aria-label="Who Not How">Who Not How</a><hr />
                  </li>
                  <li data-name="flyout-settings" aria-label="Settings">
                    <a href="/signup" data-name="flyout-link-settings" aria-label="Settings">Settings</a><hr />
                  </li>
                  <li data-name="flyout-preferences" aria-label="Preferences">
                    <a href="/setup/preferences" data-name="flyout-link-preferences" aria-label="Preferences">Preferences</a><hr />
                  </li>
                  <li data-name="flyout-orientation" aria-label="Orientation Videos">
                    <a href="/setup/orientationVideo" data-name="flyout-link-orientation" aria-label="Orientation Videos">Orientation Videos</a><hr />
                  </li>
                  <li data-name="flyout-signout" aria-label="Sign out">
                    <a
                      href={guestMode === true && settings?.homepage ? settings.homepage : "/signin"}
                      data-name="flyout-link-signout"
                      aria-label="Sign out"
                    >
                      Sign out
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>
          {/* Rendered into document.body (a portal): inside the header it was trapped by ancestor
              stacking and positioning, so clicks could miss it. */}
          {isSettingsOpen &&
            createPortal(
            <div
              className="settings-panel-overlay"
              data-name="settings-panel-overlay"
              onClick={cancelSettings}
            >
              <div
                className="settings-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="settings-panel-title"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="settings-panel-close"
                  aria-label="Close settings"
                  onClick={cancelSettings}
                >
                  &times;
                </button>
                <h2 id="settings-panel-title">Settings</h2>
                {showAccount && (
                  <section className="settings-card" aria-labelledby="settings-account-title">
                    <h3 id="settings-account-title" className="settings-card-title">Account</h3>
                    <AccountAvatarSection userId={user.id} token={authToken} currentUrl={avatarShown} onChanged={onAvatarChanged} />
                  </section>
                )}
                <section className="settings-card" aria-labelledby="settings-mic-title">
                  <h3 id="settings-mic-title" className="settings-card-title">Microphone</h3>
                <div className="settings-panel-group" role="radiogroup" aria-label="Microphone">
                  <label className="settings-panel-option" onClick={() => chooseMicMode('hf')}>
                    <input
                      type="radio"
                      name="mic-mode"
                      value="hf"
                      checked={micModeChoice === 'hf'}
                      onChange={() => chooseMicMode('hf')}
                    />
                    <span>
                      <strong>Hands-free</strong>
                      <small>The mic stays on and sends when you pause. You can talk over the assistant to interrupt it.</small>
                    </span>
                  </label>
                  <label className="settings-panel-option" onClick={() => chooseMicMode('ptt')}>
                    <input
                      type="radio"
                      name="mic-mode"
                      value="ptt"
                      checked={micModeChoice === 'ptt'}
                      onChange={() => chooseMicMode('ptt')}
                    />
                    <span>
                      <strong>Push-to-Talk</strong>
                      <small>Hold the mic to talk, or tap once to keep it open and tap again to send.</small>
                    </span>
                  </label>
                </div>
                </section>
                <div className="settings-panel-actions">
                  <button type="button" className="ui-button" onClick={cancelSettings}>
                    Cancel
                  </button>
                  <button type="button" className="ui-button" onClick={saveSettings}>
                    OK
                  </button>
                </div>
              </div>
            </div>,
              document.body
            )}
          <HistoryModal
            isOpen={isHistoryOpen}
            onClose={() => setHistoryOpen(false)}
            onLoad={(items: any) => console.log('Loading:', items)}
          />
        </div>
      </header>
      <div style={{ color: "white", width: "100vw", height: "2px", position: "absolute", marginTop: "11.5vh"}}>
        <hr id="hr-header"/>
      </div>
    </>
  );
}
